import { useState, useEffect, useMemo, Fragment } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { payrollApi } from '../../services/api';
import { PageHeader, Spinner, EmptyState, Modal, ConfirmModal } from '../../components/shared/index';
import {
  ArrowLeftIcon, CheckBadgeIcon, BoltIcon, ChevronDownIcon, ChevronRightIcon,
  PencilSquareIcon, PlusIcon, TrashIcon, ArrowDownTrayIcon, CpuChipIcon,
} from '@heroicons/react/24/outline';

const STATUS_STYLES = {
  draft:       'bg-gray-100  text-gray-700  border-gray-200',
  in_progress: 'bg-amber-100 text-amber-800 border-amber-200',
  approved:    'bg-blue-100  text-blue-800  border-blue-200',
  exported:    'bg-green-100 text-green-800 border-green-200',
};

const EXPORT_FORMATS = [
  { key: 'gusto',     label: 'Gusto' },
  { key: 'adp',       label: 'ADP' },
  { key: 'rippling',  label: 'Rippling' },
  { key: 'csv',       label: 'Generic CSV' },
];

const ADJUSTMENT_TYPES = ['bonus', 'deduction', 'reimbursement', 'correction', 'other'];

function StatusBadge({ status }) {
  return (
    <span className={clsx(
      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize',
      STATUS_STYLES[status] || STATUS_STYLES.draft,
    )}>
      {String(status || 'draft').replace('_', ' ')}
    </span>
  );
}

function fmtMoney(n, currency = 'USD') {
  const num = Number(n) || 0;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(num);
  } catch {
    return `${currency} ${num.toFixed(2)}`;
  }
}

function RunEditModal({ open, onClose, onSubmit, pending, run }) {
  const [form, setForm] = useState({ base_amount: '', bonus_amount: '', notes: '' });

  useEffect(() => {
    if (run) {
      setForm({
        base_amount:  run.base_amount ?? '',
        bonus_amount: run.bonus_amount ?? '',
        notes:        run.notes ?? '',
      });
    }
  }, [run]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Modal open={open} onClose={onClose} title={`Edit run — ${run?.employee?.name || ''}`} size="md">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Base amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input"
              value={form.base_amount}
              onChange={(e) => set('base_amount', e.target.value)}
            />
          </div>
          <div>
            <label className="label">Bonus amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input"
              value={form.bonus_amount}
              onChange={(e) => set('bonus_amount', e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="label">Notes</label>
          <textarea
            className="input h-24 resize-none"
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Any adjustments or payroll notes…"
          />
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            disabled={pending}
            onClick={() => onSubmit({
              base_amount:  form.base_amount === '' ? undefined : Number(form.base_amount),
              bonus_amount: form.bonus_amount === '' ? undefined : Number(form.bonus_amount),
              notes:        form.notes || null,
            })}
            className="btn-primary disabled:opacity-40"
          >
            {pending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AdjustmentForm({ onSubmit, pending }) {
  const [form, setForm] = useState({ type: 'bonus', description: '', amount: '' });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.type && form.description && form.amount !== '' && !Number.isNaN(Number(form.amount));

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
      <div className="md:col-span-3">
        <label className="label">Type</label>
        <select className="input capitalize" value={form.type} onChange={(e) => set('type', e.target.value)}>
          {ADJUSTMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div className="md:col-span-5">
        <label className="label">Description</label>
        <input
          className="input"
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="e.g. Referral bonus"
        />
      </div>
      <div className="md:col-span-2">
        <label className="label">Amount</label>
        <input
          type="number"
          step="0.01"
          className="input"
          value={form.amount}
          onChange={(e) => set('amount', e.target.value)}
        />
      </div>
      <div className="md:col-span-2">
        <button
          disabled={!valid || pending}
          onClick={() => {
            onSubmit({ type: form.type, description: form.description, amount: Number(form.amount) });
            setForm({ type: 'bonus', description: '', amount: '' });
          }}
          className="btn-primary w-full disabled:opacity-40"
        >
          <PlusIcon className="w-4 h-4" /> Add
        </button>
      </div>
    </div>
  );
}

function runTotal(run) {
  const base  = Number(run.base_amount)  || 0;
  const bonus = Number(run.bonus_amount) || 0;
  const adj   = (run.adjustments || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
  return { base, bonus, adj, total: base + bonus + adj };
}

export default function PayrollDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [expanded, setExpanded]   = useState(new Set());
  const [editingRun, setEditingRun] = useState(null);
  const [confirmAdj, setConfirmAdj] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);

  const { data: period, isLoading } = useQuery({
    queryKey: ['payroll', 'period', id],
    queryFn:  () => payrollApi.period(id).then((r) => r.data),
  });

  const generate = useMutation({
    mutationFn: () => payrollApi.generateRuns(id),
    onSuccess:  () => { toast.success('Runs generated'); qc.invalidateQueries({ queryKey: ['payroll', 'period', id] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to generate runs'),
  });

  const approve = useMutation({
    mutationFn: () => payrollApi.approve(id),
    onSuccess:  () => { toast.success('Payroll approved'); qc.invalidateQueries({ queryKey: ['payroll'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to approve'),
  });

  const exportPeriod = useMutation({
    mutationFn: (format) => payrollApi.exportPeriod(id, format).then((res) => ({ res, format })),
    onSuccess:  ({ res, format }) => {
      try {
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        const safeName = (period?.name || 'payroll').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
        a.download = `${safeName}-${format}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Exported as ${format.toUpperCase()}`);
        setExportOpen(false);
        qc.invalidateQueries({ queryKey: ['payroll'] });
      } catch {
        toast.error('Download failed');
      }
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Export failed'),
  });

  const updateRun = useMutation({
    mutationFn: ({ runId, data }) => payrollApi.updateRun(runId, data),
    onSuccess:  () => { toast.success('Run updated'); setEditingRun(null); qc.invalidateQueries({ queryKey: ['payroll', 'period', id] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to update run'),
  });

  const addAdj = useMutation({
    mutationFn: ({ runId, data }) => payrollApi.addAdjustment(runId, data),
    onSuccess:  () => { toast.success('Adjustment added'); qc.invalidateQueries({ queryKey: ['payroll', 'period', id] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to add adjustment'),
  });

  const deleteAdj = useMutation({
    mutationFn: (adjId) => payrollApi.deleteAdjustment(adjId),
    onSuccess:  () => { toast.success('Adjustment removed'); qc.invalidateQueries({ queryKey: ['payroll', 'period', id] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to remove adjustment'),
  });

  const runs = period?.runs || [];
  const currency = period?.currency || 'USD';

  const totals = useMemo(() => {
    return runs.reduce((acc, r) => {
      const t = runTotal(r);
      acc.base  += t.base;
      acc.bonus += t.bonus;
      acc.adj   += t.adj;
      acc.total += t.total;
      return acc;
    }, { base: 0, bonus: 0, adj: 0, total: 0 });
  }, [runs]);

  const toggle = (runId) => setExpanded((prev) => {
    const n = new Set(prev);
    if (n.has(runId)) n.delete(runId); else n.add(runId);
    return n;
  });

  if (isLoading) {
    return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  }

  if (!period) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <EmptyState
          title="Period not found"
          description="This payroll period may have been deleted."
          action={<button onClick={() => navigate('/app/payroll')} className="btn-primary">Back to payroll</button>}
        />
      </div>
    );
  }

  const canGenerate = period.status === 'draft';
  const canApprove  = period.status === 'in_progress';
  const canEdit     = ['draft', 'in_progress'].includes(period.status);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Link to="/app/payroll" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3">
        <ArrowLeftIcon className="w-4 h-4" /> Back to payroll
      </Link>

      <PageHeader
        title={period.name}
        subtitle={`${new Date(period.period_start).toLocaleDateString()} → ${new Date(period.period_end).toLocaleDateString()}${period.pay_date ? ` · Pay ${new Date(period.pay_date).toLocaleDateString()}` : ''}`}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={period.status} />
            <button
              onClick={() => navigate(`/app/payroll/${id}/engine`)}
              className="btn-primary"
              title="Run the real payroll engine"
            >
              <CpuChipIcon className="w-4 h-4" /> Go to Engine
            </button>
            {canGenerate && (
              <button
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
                className="btn-secondary"
              >
                <BoltIcon className="w-4 h-4" />
                {generate.isPending ? 'Generating…' : 'Generate runs'}
              </button>
            )}
            {canApprove && (
              <button
                onClick={() => setConfirmApprove(true)}
                className="btn-primary"
              >
                <CheckBadgeIcon className="w-4 h-4" /> Approve
              </button>
            )}
            <div className="relative">
              <button
                onClick={() => setExportOpen((o) => !o)}
                disabled={exportPeriod.isPending}
                className="btn-secondary"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {exportPeriod.isPending ? 'Exporting…' : 'Export'}
                <ChevronDownIcon className="w-3.5 h-3.5" />
              </button>
              {exportOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                  {EXPORT_FORMATS.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => exportPeriod.mutate(f.key)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        }
      />

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Base</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{fmtMoney(totals.base, currency)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Bonus</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{fmtMoney(totals.bonus, currency)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Adjustments</p>
          <p className={clsx('text-xl font-bold mt-1', totals.adj < 0 ? 'text-red-600' : 'text-gray-900')}>
            {fmtMoney(totals.adj, currency)}
          </p>
        </div>
        <div className="card p-4 bg-blue-50/40">
          <p className="text-xs text-blue-700 uppercase tracking-wide font-semibold">Total</p>
          <p className="text-xl font-bold text-blue-700 mt-1">{fmtMoney(totals.total, currency)}</p>
          <p className="text-xs text-blue-700/70 mt-0.5">{runs.length} run{runs.length === 1 ? '' : 's'}</p>
        </div>
      </div>

      {/* Runs */}
      <div className="card p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="section-title">Runs</h2>
          {canGenerate && runs.length === 0 && (
            <span className="text-xs text-gray-500">Click Generate runs above to populate from active employees.</span>
          )}
        </div>

        {runs.length === 0 ? (
          <EmptyState
            title="No runs yet"
            description={canGenerate
              ? 'Generate runs to populate this period from active employees and the latest merit cycle.'
              : 'This period has no runs. Runs are added when you generate the period.'}
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="th w-8" />
                  {['Employee', 'Base', 'Bonus', 'Adjustments', 'Total', 'Currency', 'Actions'].map((h) => (
                    <th key={h} className="th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {runs.map((r) => {
                  const t = runTotal(r);
                  const isOpen = expanded.has(r.id);
                  const adjustments = r.adjustments || [];
                  return (
                    <Fragment key={r.id}>
                      <tr className="tr-hover">
                        <td className="td">
                          <button
                            onClick={() => toggle(r.id)}
                            className="p-0.5 rounded hover:bg-gray-100 text-gray-500"
                            title={isOpen ? 'Collapse' : 'Expand adjustments'}
                          >
                            {isOpen ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="td">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">
                              {r.employee?.name?.[0]?.toUpperCase() ?? '?'}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{r.employee?.name || '—'}</div>
                              {r.employee?.email && <div className="text-xs text-gray-500">{r.employee.email}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="td text-sm">{fmtMoney(t.base, currency)}</td>
                        <td className="td text-sm">{fmtMoney(t.bonus, currency)}</td>
                        <td className="td text-sm">
                          <span className={clsx(t.adj < 0 && 'text-red-600')}>
                            {fmtMoney(t.adj, currency)}
                          </span>
                          {adjustments.length > 0 && (
                            <span className="ml-1 text-xs text-gray-400">({adjustments.length})</span>
                          )}
                        </td>
                        <td className="td text-sm font-semibold text-gray-900">{fmtMoney(t.total, currency)}</td>
                        <td className="td text-xs text-gray-500">{r.currency || currency}</td>
                        <td className="td">
                          {canEdit && (
                            <button
                              onClick={() => setEditingRun(r)}
                              className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                              title="Edit run"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>

                      {isOpen && (
                        <tr>
                          <td className="td" />
                          <td colSpan={7} className="td bg-gray-50/60">
                            <div className="space-y-3">
                              <div>
                                <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                  Adjustments ({adjustments.length})
                                </h4>
                                {adjustments.length === 0 ? (
                                  <p className="text-sm text-gray-500">No adjustments on this run.</p>
                                ) : (
                                  <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg bg-white">
                                    {adjustments.map((a) => (
                                      <li key={a.id} className="flex items-center justify-between px-3 py-2 text-sm">
                                        <div className="flex items-center gap-2">
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border border-gray-200 bg-gray-50 capitalize">
                                            {a.type}
                                          </span>
                                          <span className="text-gray-700">{a.description}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                          <span className={clsx(
                                            'font-medium',
                                            Number(a.amount) < 0 ? 'text-red-600' : 'text-gray-900',
                                          )}>
                                            {fmtMoney(a.amount, currency)}
                                          </span>
                                          {canEdit && (
                                            <button
                                              onClick={() => setConfirmAdj(a)}
                                              className="p-1 rounded hover:bg-red-50 text-red-600"
                                              title="Remove"
                                            >
                                              <TrashIcon className="w-4 h-4" />
                                            </button>
                                          )}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              {canEdit && (
                                <div className="border-t border-gray-200 pt-3">
                                  <AdjustmentForm
                                    pending={addAdj.isPending}
                                    onSubmit={(data) => addAdj.mutate({ runId: r.id, data })}
                                  />
                                </div>
                              )}
                              {r.notes && (
                                <div className="text-xs text-gray-500 border-t border-gray-200 pt-3">
                                  <span className="font-semibold text-gray-600">Notes: </span>{r.notes}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RunEditModal
        open={!!editingRun}
        onClose={() => setEditingRun(null)}
        onSubmit={(data) => updateRun.mutate({ runId: editingRun.id, data })}
        pending={updateRun.isPending}
        run={editingRun}
      />

      <ConfirmModal
        open={!!confirmAdj}
        onClose={() => setConfirmAdj(null)}
        onConfirm={() => deleteAdj.mutate(confirmAdj.id)}
        title="Remove adjustment?"
        message={`This will remove "${confirmAdj?.description}" (${fmtMoney(confirmAdj?.amount, currency)}) from the run.`}
        danger
      />

      <ConfirmModal
        open={confirmApprove}
        onClose={() => setConfirmApprove(false)}
        onConfirm={() => approve.mutate()}
        title="Approve this payroll?"
        message="Approving locks the runs for export. You can still add adjustments until it's exported."
      />
    </div>
  );
}
